import React from 'react';

/**
 * A QR code component that applies the specified color to the SVG
 * @param {string} color - The color to apply to the QR code (hex, rgb, etc.)
 * @param {string} className - CSS classes to apply to the SVG
 * @param {object} style - Additional inline styles
 * @param {boolean} border - Whether to show separated corner borders (default: false)
 */
const GenericQrCode = ({
  color = '#000000',
  className = '',
  style = {},
  border = false,
}) => {
  // Using a larger viewBox for bordered version to provide more space
  const viewBox = border ? '0 0 150 150' : '0 0 100 100';

  // Calculate offsets to center the QR code in the viewBox when bordered
  const offset = border ? 25 : 0;

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      viewBox={viewBox}
      className={className}
      style={{ ...style, fill: color }}
    >
      {/* Optional border with corners that don't touch */}
      {border && (
        <>
          {/* Top left corner - moderately extended */}
          <path
            d='M6,45 C6,18 18,6 45,6'
            fill='none'
            stroke={color}
            strokeWidth='20'
            strokeLinecap='round'
          />

          {/* Top right corner - moderately extended */}
          <path
            d='M105,6 C132,6 144,18 144,45'
            fill='none'
            stroke={color}
            strokeWidth='20'
            strokeLinecap='round'
          />

          {/* Bottom left corner - moderately extended */}
          <path
            d='M6,105 C6,132 18,144 45,144'
            fill='none'
            stroke={color}
            strokeWidth='20'
            strokeLinecap='round'
          />

          {/* Bottom right corner - moderately extended */}
          <path
            d='M105,144 C132,144 144,132 144,105'
            fill='none'
            stroke={color}
            strokeWidth='20'
            strokeLinecap='round'
          />
        </>
      )}

      <g transform={`translate(${offset}, ${offset})`}>
        {/* Position detection patterns (corners) */}
        {/* Top-left position detection pattern */}
        <rect x='8' y='8' width='22' height='22' />
        <rect x='12' y='12' width='14' height='14' fill='white' />
        <rect x='16' y='16' width='6' height='6' />

        {/* Top-right position detection pattern */}
        <rect x='70' y='8' width='22' height='22' />
        <rect x='74' y='12' width='14' height='14' fill='white' />
        <rect x='78' y='16' width='6' height='6' />

        {/* Bottom-left position detection pattern */}
        <rect x='8' y='70' width='22' height='22' />
        <rect x='12' y='74' width='14' height='14' fill='white' />
        <rect x='16' y='78' width='6' height='6' />

        {/* Timing patterns and alignment patterns */}
        {/* Horizontal timing pattern */}
        <path d='M32,14 H68' strokeWidth='4' />

        {/* Vertical timing pattern */}
        <path d='M14,32 V68' strokeWidth='4' />

        {/* Format information area - Connected L-shapes and patterns */}
        <path d='M30,8 H38 V16 H30 Z' />
        <path d='M46,8 H54 V16 H46 Z' />
        <path d='M62,8 H70 V16 H62 Z' />

        <path d='M8,30 H16 V38 H8 Z' />
        <path d='M8,46 H16 V54 H8 Z' />
        <path d='M8,62 H16 V70 H8 Z' />

        {/* Alignment pattern bottom-right */}
        <rect x='76' y='76' width='12' height='12' />
        <rect x='79' y='79' width='6' height='6' fill='white' />
        <rect x='81' y='81' width='2' height='2' />

        {/* Central data patterns with more realistic connected shapes */}
        {/* Connected blocks in center */}
        <path d='M32,32 H40 V40 H48 V48 H40 V56 H32 Z' />

        {/* Connected L shapes */}
        <path d='M52,28 H68 V36 H60 V44 H52 Z' />
        <path d='M72,44 H80 V60 H72 V52 H64 V44 H72 Z' />

        {/* T shapes */}
        <path d='M44,52 H52 V68 H44 V52 Z M40,56 H56 V64 H40 V56 Z' />

        {/* U shapes */}
        <path d='M60,52 H68 V68 H60 V52 Z M60,60 H76 V68 H60 V60 Z' />

        {/* Scattered individual blocks for additional data density */}
        <rect x='28' y='20' width='8' height='8' />
        <rect x='52' y='20' width='8' height='8' />
        <rect x='64' y='20' width='8' height='8' />

        <rect x='20' y='28' width='8' height='8' />
        <rect x='44' y='28' width='8' height='8' />

        <rect x='20' y='44' width='8' height='8' />
        <rect x='80' y='28' width='8' height='8' />

        <rect x='20' y='64' width='8' height='8' />
        <rect x='28' y='76' width='8' height='8' />

        <rect x='44' y='76' width='8' height='8' />
        <rect x='60' y='76' width='8' height='8' />

        <rect x='28' y='84' width='8' height='8' />
        <rect x='52' y='84' width='8' height='8' />
        <rect x='68' y='84' width='8' height='8' />
        <rect x='84' y='84' width='8' height='8' />

        {/* Diagonal patterns */}
        <path d='M76,36 L84,44' strokeWidth='6' strokeLinecap='square' />
        <path d='M36,76 L44,84' strokeWidth='6' strokeLinecap='square' />

        {/* Small dots for fine details */}
        <circle cx='58' cy='42' r='3' />
        <circle cx='42' cy='58' r='3' />
        <circle cx='74' cy='58' r='3' />
      </g>
    </svg>
  );
};

export default GenericQrCode;
